import type { SegmentResult } from '@/core/data/spark/spark.interface';
import { addDays, addHours, addMinutes, differenceInHours, parseISO, setMinutes, subMinutes } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

/** Next/previous calendar `yyyy-MM-dd` in an IANA zone (noon anchor avoids most DST edges). */
export function addCalendarDaysToDateKey(dateKey: string, timeZone: string, days: number): string {
  const anchorUtc = fromZonedTime(`${dateKey}T12:00:00`, timeZone);
  return formatInTimeZone(addDays(anchorUtc, days), timeZone, 'yyyy-MM-dd');
}

/**
 * Spark proposal `start` / `end` from the API are stored as UTC in the DB and usually
 * serialized as ISO-8601 with `Z` (or another explicit offset). For those values, use
 * {@link parseISO} — do not pass them to {@link fromZonedTime}: for strings ending in
 * `Z`, date-fns-tz reinterprets using the browser's local calendar fields, which shifts
 * times for users not in UTC (e.g. +2h in Europe/Berlin).
 *
 * Wall-clock strings without an offset (e.g. `yyyy-MM-dd HH:mm` from older clients) are
 * interpreted in `wallClockTimeZone` (segment / intent IANA zone).
 *
 * TODO: This should be generalized to be used in different cases where we need to handle timezones.
 */
export function parseSparkProposalInstant(value: string, wallClockTimeZone: string): Date {
  const trimmed = value.trim();
  const hasExplicitOffset = /Z$/i.test(trimmed) || /[+-]\d{2}:\d{2}$/.test(trimmed) || /[+-]\d{4}$/.test(trimmed);

  if (hasExplicitOffset) {
    return parseISO(trimmed);
  }

  const normalized = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
  return fromZonedTime(normalized, wallClockTimeZone);
}

export function getProposalTime(segment: SegmentResult) {
  const segmentStart = new Date(segment.start);
  const segmentEnd = new Date(segment.end);

  const durationHours = differenceInHours(segmentEnd, segmentStart);

  const midPoint = addHours(segmentStart, durationHours / 2);

  const rawStart = durationHours < 2 ? segmentStart : setMinutes(subMinutes(midPoint, 60), 0);
  const rawEnd = durationHours < 2 ? segmentEnd : setMinutes(addMinutes(midPoint, 60), 0);

  const start = rawStart < segmentStart ? segmentStart : rawStart;
  const end = rawEnd > segmentEnd ? segmentEnd : rawEnd;

  return { start, end };
}
